import React, { useState, useEffect, useRef } from 'react';
import ModalShell from '../components/ModalShell';
import { Key, Calendar, User, Phone, Shield, Wallet, CheckCircle, Eye, EyeOff, Loader2, Building2, Save } from 'lucide-react';
import { API_URL } from '../config';
import { safeFetch } from '../utils/safeFetch';
import useModalBackButton from '../hooks/useModalBackButton';
import useToast from '../hooks/useToast';
import { USER_STAGE_ORDER, STAGES_CONFIG, STAGE_BADGE_STYLES, getStagesFromRoles } from '../utils/stages';
import { useDryCleaningEnabled } from '../utils/featureFlags';
import { haptic } from '../utils/vibration';
import { formatPersonName } from '../utils/personName';
import FloatingField from '../components/FloatingField';

const PasswordChangeModal = ({ 
  showPasswordModal, 
  handlePasswordSubmit, 
  passwordData, 
  handlePasswordChange, 
  showPasswords, 
  togglePasswordVisibility, 
  loading, 
  resetPasswordModal 
}) => {
  useModalBackButton(showPasswordModal, resetPasswordModal);

  return (
    <ModalShell
      open={showPasswordModal}
      onClose={resetPasswordModal}
      variant="window"
      panelClassName="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md shadow-xl"
    >
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-xl flex items-center justify-center">
          <Key className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Смена пароля
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Введите текущий и новый пароль
          </p>
        </div>
      </div>
      
      <div className="space-y-4">
        {/* Текущий пароль */}
        <div>
          <FloatingField label="Текущий пароль *">
            <div className="relative">
            <input
              type={showPasswords.current ? 'text' : 'password'}
              value={passwordData.currentPassword}
              onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
              className="w-full select-text px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:text-gray-100"
              placeholder="Введите текущий пароль"
            />
            <button
              type="button"
              onClick={() => {
                haptic('selection');
                togglePasswordVisibility('current');
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
            </div>
          </FloatingField>
        </div>
        
        {/* Новый пароль */}
        <div>
          <FloatingField label="Новый пароль *">
            <div className="relative">
            <input
              type={showPasswords.new ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
              className="w-full select-text px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:text-gray-100"
              placeholder="Введите новый пароль"
            />
            <button
              type="button"
              onClick={() => {
                haptic('selection');
                togglePasswordVisibility('new');
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
            </div>
          </FloatingField>
        </div>
        
        {/* Подтверждение пароля */}
        <div>
          <FloatingField label="Подтверждение нового пароля *">
            <div className="relative">
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
              className="w-full select-text px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 dark:text-gray-100"
              placeholder="Повторите новый пароль"
            />
            <button
              type="button"
              onClick={() => {
                haptic('selection');
                togglePasswordVisibility('confirm');
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
            </div>
          </FloatingField>
        </div>
      </div>
      
      {/* Кнопки */}
      <div className="flex items-center gap-3 mt-6">
        <button
          onClick={() => {
            haptic('close');
            resetPasswordModal();
          }}
          className="flex-1 px-6 h-12 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium"
        >
          Отмена
        </button>
        <button
          onClick={() => {
            haptic('medium');
            handlePasswordSubmit();
          }}
          disabled={loading}
          className="flex-[2] px-6 h-12 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 font-medium"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          <span>{loading ? 'Сохранение...' : 'Сохранить'}</span>
        </button>
      </div>
    </ModalShell>
  );
};

const Profile = ({ currentUser, userRoles }) => {
  const isDryCleaningEnabled = useDryCleaningEnabled();
  const [userData, setUserData] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loadingPassword, setLoadingPassword] = useState(false);
  const { toast, showToast, hideToast } = useToast(3500);
  const longPressTimerRef = useRef(null);

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);

  const fetchUserData = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');

      // Получаем данные текущего пользователя через check-auth (работает для всех ролей)
      const authData = await safeFetch(`${API_URL}/api/check-auth`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Если пользователь admin/developer — получаем полную информацию
      if (authData.user_type === 'admin' || authData.user_type === 'developer') {
        const userData = await safeFetch(`${API_URL}/api/users/${authData.user_id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setUserData(userData);
        // Загружаем подразделения если есть
        if (userData.departments) {
          await fetchDepartments(JSON.parse(userData.departments));
        }
      } else {
        // Для обычных пользователей используем данные из check-auth
        const departmentIds = authData.departments ? JSON.parse(authData.departments) : [];
        setUserData({
          id: authData.user_id,
          login: authData.login,
          first_name: authData.first_name,
          last_name: authData.last_name,
          phone: authData.phone,
          user_type: authData.user_type,
          balance: authData.balance || 0,
          created_date: authData.created_date,
          user_roles: authData.user_roles,
          available_menus: authData.available_menus,
          can_access_deferred_orders: authData.can_access_deferred_orders,
          departments: authData.departments
        });
        // Загружаем подразделения если есть
        if (departmentIds.length > 0) {
          await fetchDepartments(departmentIds);
        }
      }
    } catch (err) {
      console.error('Ошибка загрузки данных пользователя:', err);
      setError(err.message || 'Ошибка загрузки данных пользователя');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async (departmentIds) => {
    try {
      const token = localStorage.getItem('token');
      const data = await safeFetch(`${API_URL}/api/departments/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      // Фильтруем только те подразделения, которые назначены пользователю
      const userDepts = data.filter(d => departmentIds.includes(d.id));
      setDepartments(userDepts);
    } catch (err) {
      console.error('Ошибка загрузки подразделений:', err);
    }
  };

  const getAvailableStages = () => {
    const stageIds = getStagesFromRoles(userData?.user_roles || '[]');
    const userType = String(userData?.user_type || '').trim().toLowerCase();
    const canAccessDeferredOrders = ['dispatcher', 'admin', 'developer'].includes(userType)
      && (typeof userData?.can_access_deferred_orders === 'boolean'
        ? userData.can_access_deferred_orders
        : ['admin', 'developer'].includes(userType));

    const stages = USER_STAGE_ORDER
      .filter(stageId => isDryCleaningEnabled || stageId !== 'cleaning')
      .filter(stageId => stageIds.includes(stageId))
      .map(stageId => ({ id: stageId, ...STAGES_CONFIG[stageId] }))
      .filter(Boolean);

    if (canAccessDeferredOrders && STAGES_CONFIG.deferred) {
      stages.push({ id: 'deferred', ...STAGES_CONFIG.deferred });
    }

    return stages;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Moscow'
    });
  };

  const formatBalanceDisplay = (amount) => {
    const value = Number(amount || 0);
    const absValue = Math.abs(value);

    if (absValue >= 1000000) {
      return `${(value / 1000000).toFixed(1)} млн ₽`;
    }

    if (absValue >= 10000) {
      return `${(value / 1000).toFixed(1)} тыс ₽`;
    }

    return `${value.toFixed(2)} ₽`;
  };

  const getUserAvatar = (firstName, lastName) => {
    const firstLetter = firstName?.[0]?.toUpperCase() || '';
    const lastLetter = lastName?.[0]?.toUpperCase() || '';
    return firstLetter + lastLetter || 'U';
  };

  const copyToClipboard = async (text, successMessage, fieldKey) => {
    const value = String(text || '').trim();
    if (!value) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      haptic('success');
      showToast(successMessage, 'success');
    } catch (error) {
      showToast('Не удалось скопировать', 'error');
    }
  };

  const startLongPress = (callback) => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      callback();
      longPressTimerRef.current = null;
    }, 450);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const bindLongPress = (callback) => ({
    onMouseDown: () => startLongPress(callback),
    onMouseUp: cancelLongPress,
    onMouseLeave: cancelLongPress,
    onTouchStart: () => startLongPress(callback),
    onTouchEnd: cancelLongPress,
    onTouchCancel: cancelLongPress,
    onContextMenu: (e) => e.preventDefault()
  });

  const availableStages = getAvailableStages();

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const resetPasswordModal = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswords({
      current: false,
      new: false,
      confirm: false
    });
    setShowPasswordModal(false);
  };

  const handlePasswordSubmit = async () => {
    // Валидация
    if (!passwordData.currentPassword) {
      showToast('Введите текущий пароль', 'error');
      return;
    }
    if (!passwordData.newPassword) {
      showToast('Введите новый пароль', 'error');
      return;
    }
    if (passwordData.newPassword.length < 7) {
      showToast('Новый пароль должен содержать минимум 7 символов', 'error');
      return;
    }
    if (!/[a-zа-я]/.test(passwordData.newPassword)) {
      showToast('Новый пароль должен содержать хотя бы одну маленькую букву', 'error');
      return;
    }
    if (!/[A-ZА-Я]/.test(passwordData.newPassword)) {
      showToast('Новый пароль должен содержать хотя бы одну заглавную букву', 'error');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showToast('Пароли не совпадают', 'error');
      return;
    }
    if (passwordData.currentPassword === passwordData.newPassword) {
      showToast('Новый пароль должен отличаться от текущего', 'error');
      return;
    }

    setLoadingPassword(true);

    try {
      const token = localStorage.getItem('token');
      
      await safeFetch(`${API_URL}/api/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });

      showToast('Пароль успешно изменён', 'success');
      haptic('success');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Закрываем модальное окно через 2 секунды
      setTimeout(() => {
        resetPasswordModal();
      }, 2000);
      
    } catch (err) {
      console.error('Ошибка смены пароля:', err);
      showToast(err.message || 'Ошибка смены пароля', 'error');
    } finally {
      setLoadingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-sm animate-pulse">
              <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
            </div>
            <div className="space-y-1">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow-sm">
          <div className="text-red-600 dark:text-red-400 text-sm">
            <p>Ошибка загрузки данных профиля: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 select-none desktop-content-container">
      <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow-sm">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-base">
            {getUserAvatar(userData?.first_name, userData?.last_name)}
          </div>
          <div>
            <h2 className="font-bold text-gray-900 dark:text-gray-100 text-base">
              {formatPersonName(userData, userData?.login || '')}
            </h2>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div
            className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3"
            {...bindLongPress(() => copyToClipboard(userData?.login || '', 'Логин скопирован', 'login'))}
          >
            <div className="flex items-center text-blue-600 dark:text-blue-300 mb-1">
              <User className="w-4 h-4 mr-1" />
              <span className="text-xs">Логин</span>
            </div>
              <div className="break-all text-sm font-semibold leading-tight text-gray-900 dark:text-gray-100 sm:text-base">
                {userData?.login || '—'}
              </div>
            </div>
          
          <div
            className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3"
            {...bindLongPress(() => copyToClipboard(Number(userData?.balance || 0).toFixed(2), 'Баланс скопирован', 'balance'))}
          >
            <div className="flex items-center text-green-600 dark:text-green-300 mb-1">
              <Wallet className="w-4 h-4 mr-1" />
              <span className="text-xs">Баланс</span>
            </div>
              <div className="text-sm font-semibold leading-tight text-gray-900 dark:text-gray-100 sm:text-base">
                <span className="block truncate sm:hidden" title={`${Number(userData?.balance || 0).toFixed(2)} ₽`}>
                  {formatBalanceDisplay(userData?.balance)}
                </span>
                <span className="hidden break-words sm:block" title={`${Number(userData?.balance || 0).toFixed(2)} ₽`}>
                  {Number(userData?.balance || 0).toFixed(2)} ₽
                </span>
              </div>
            </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-md">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
              <span className="text-gray-700 dark:text-gray-300 text-sm">Регистрация</span>
            </div>
            <span className="text-gray-900 dark:text-gray-100 text-sm font-medium">
              {formatDate(userData?.created_date)}
            </span>
          </div>
          
          <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-md">
            <div className="flex items-center">
              <Shield className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
              <span className="text-gray-700 dark:text-gray-300 text-sm">Роль</span>
            </div>
            <span className="text-gray-900 dark:text-gray-100 text-sm font-medium">
              {userRoles[userData?.user_type]}
            </span>
          </div>
          
          {userData?.phone && (
            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-md">
              <div className="flex items-center">
                <Phone className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
                <span className="text-gray-700 dark:text-gray-300 text-sm">Телефон</span>
              </div>
              <span className="text-gray-900 dark:text-gray-100 text-sm font-medium">
                {userData.phone}
              </span>
            </div>
          )}
        </div>

        {/* Доступные этапы пользователя */}
        {availableStages.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-3">
              <CheckCircle className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
              <span className="text-gray-700 dark:text-gray-300 text-sm font-medium">Доступные этапы</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {availableStages.map((stage) => {
                const Icon = stage.icon;
                const badgeClass = STAGE_BADGE_STYLES[stage.color] || STAGE_BADGE_STYLES.blue;
                return (
                  <span
                    key={stage.id}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${badgeClass}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {stage.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Подразделения пользователя */}
        {departments.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-3">
              <Building2 className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
              <span className="text-gray-700 dark:text-gray-300 text-sm font-medium">Подразделения</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {departments.map((dept) => (
                <span
                  key={dept.id}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${
                    dept.is_active
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {dept.name}
                  {!dept.is_active && (
                    <span className="ml-1.5 text-xs opacity-70">(неактивно)</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Кнопка смены пароля */}
      <button
        onClick={() => {
          haptic('open');
          setShowPasswordModal(true);
        }}
        className="w-full bg-blue-600 text-white rounded-lg py-2 px-4 text-base font-medium hover:bg-blue-700 transition-all duration-300 flex items-center justify-center space-x-2 shadow"
      >
        <Key className="w-5 h-5" />
        <span>Сменить пароль</span>
      </button>
      
      {/* Модальное окно смены пароля */}
      <PasswordChangeModal
        showPasswordModal={showPasswordModal}
        handlePasswordSubmit={handlePasswordSubmit}
        passwordData={passwordData}
        handlePasswordChange={handlePasswordChange}
        showPasswords={showPasswords}
        togglePasswordVisibility={togglePasswordVisibility}
        loading={loadingPassword}
        resetPasswordModal={resetPasswordModal}
      />
          </div>
  );
};

export default Profile;
